import { API_CONFIG } from '@/lib/api/config';
import { cachedConnectionCheck, clearConnectionCheckCache } from '@/lib/api/connectionCheckCache';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
});

export type UberDirectEnvironment = 'sandbox' | 'production';

export interface UberDirectAddress {
  street_address: string[];
  city: string;
  state: string;
  zip_code: string;
  country: string;
}

export interface UberDirectEndpoint {
  address: UberDirectAddress;
  name?: string;
  phone_number?: string;
  latitude?: number;
  longitude?: number;
}

export interface UberManifestItem {
  name: string;
  quantity: number;
  weight: number; // grams
  size?: string;
  price?: number;
}

export interface UberConnectPayload {
  business_account_id?: string | null;
  workspace_id?: string | null;
  environment: UberDirectEnvironment;
  customer_id: string;
  client_id: string;
  client_secret: string;
  webhook_signing_key?: string;
  account_label?: string;
}

export interface UberQuotePayload {
  business_account_id?: string | null;
  workspace_id?: string | null;
  pickup: UberDirectEndpoint;
  dropoff: UberDirectEndpoint;
}

export interface UberCreateDeliveryPayload {
  business_account_id?: string | null;
  workspace_id?: string | null;
  quote_id?: string;
  pickup: UberDirectEndpoint;
  dropoff: UberDirectEndpoint;
  manifest_items: UberManifestItem[];
  external_id?: string;
  tip?: number;
  undeliverable_action?: 'leave_at_door' | 'return' | 'discard';
  pickup_notes?: string;
  dropoff_notes?: string;
  manifest_reference?: string;
}

export interface UberListParams {
  limit?: number;
  offset?: number;
  status?: string;
}

export interface UberCourierLocation {
  lat?: number;
  lng?: number;
}

export interface UberCourierVehicle {
  make?: string;
  model?: string;
  color?: string;
  type?: string;
  [key: string]: unknown;
}

export interface UberCourier {
  name?: string;
  phone_number?: string;
  vehicle?: UberCourierVehicle | null;
  vehicle_type?: string;
  vehicle_make?: string;
  vehicle_model?: string;
  vehicle_color?: string;
  location?: UberCourierLocation;
  [key: string]: unknown;
}

export interface UberDetailedAddress {
  street_address_1?: string;
  street_address_2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  country?: string;
  [key: string]: unknown;
}

export interface UberDeliveryWaypoint {
  name?: string;
  phone_number?: string;
  address?: string | Record<string, unknown>;
  detailed_address?: UberDetailedAddress | Record<string, unknown>;
  location?: UberCourierLocation;
  status?: string;
  notes?: string;
  eta?: string;
  [key: string]: unknown;
}

export interface UberManifestDimensions {
  length?: number;
  height?: number;
  depth?: number;
  [key: string]: unknown;
}

export interface UberDeliveryManifestItem {
  name?: string;
  quantity?: number;
  weight?: number;
  size?: string;
  price?: number;
  dimensions?: UberManifestDimensions;
  [key: string]: unknown;
}

export interface UberDeliveryManifest {
  description?: string;
  total_value?: number;
  [key: string]: unknown;
}

export interface UberDeliveryDetail {
  id?: string;
  delivery_id?: string;
  quote_id?: string;
  kind?: string;
  status?: string;
  complete?: boolean;
  fee?: number;
  currency?: string;
  tracking_url?: string;
  courier_imminent?: boolean;
  pickup_eta?: string;
  dropoff_eta?: string;
  undeliverable_reason?: string;
  courier?: UberCourier | null;
  pickup?: UberDeliveryWaypoint | null;
  dropoff?: UberDeliveryWaypoint | null;
  manifest?: UberDeliveryManifest | null;
  manifest_items?: UberDeliveryManifestItem[];
  created?: string;
  updated?: string;
}

export interface UberProofBarcode {
  value?: string;
  type?: string;
  scan_result?: string;
}

export interface UberProofSignature {
  image_url?: string;
  signer_name?: string;
}

export interface UberProofPinCode {
  entered?: string;
  status?: string;
}

export type UberProofWaypoint = 'pickup' | 'dropoff' | 'return';
export type UberProofType = 'picture' | 'signature' | 'pincode';

export interface UberProofPayload {
  document?: string;
  code?: string;
  status?: string;
  [k: string]: unknown;
}

export interface UberProofOfDelivery {
  proof?: UberProofPayload;
  signature?: UberProofSignature | null;
  picture?: string | null;
  barcodes?: UberProofBarcode[];
  pin_code?: UberProofPinCode | null;
}

export type UberDirectResult<T = Record<string, unknown>> = Partial<T> & {
  ok: boolean;
  error?: string;
  code?: string;
};

const handle = async <T = Record<string, unknown>>(
  res: Response,
  fallbackMessage: string
): Promise<UberDirectResult<T>> => {
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || json?.error || json?.success === false) {
    const error = (json?.error || json?.message || fallbackMessage) as string;
    const code = json?.code as string | undefined;
    return { ok: false, error, code } as UberDirectResult<T>;
  }
  return { ok: true, ...(json as T) };
};


function appendScope(
  query: URLSearchParams,
  businessAccountId?: string | null,
  workspaceId?: string | null,
) {
  if (businessAccountId) query.append('business_account_id', businessAccountId);
  if (workspaceId) query.append('workspace_id', workspaceId);
  return query;
}

function scopeBody(businessAccountId?: string | null, workspaceId?: string | null) {
  return {
    ...(businessAccountId ? { business_account_id: businessAccountId } : {}),
    ...(workspaceId ? { workspace_id: workspaceId } : {}),
  };
}

export const uberDirectApi = {

  async getStatus(businessAccountId?: string | null, workspaceId?: string | null) {
    const scopeKey = businessAccountId || workspaceId || '';
    const result = await cachedConnectionCheck(`uber:${scopeKey}`, async () => {
      const q = appendScope(new URLSearchParams(), businessAccountId, workspaceId);
      const url = `${API_CONFIG.BASE_URL}/uber-direct/status?${q.toString()}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      return handle<{
        connected: boolean;
        environment?: UberDirectEnvironment;
        account_label?: string;
        source?: 'per_ba' | 'platform';
      }>(res, 'Failed to fetch Uber Direct status');
    });
    if (!(result as any)?.ok) clearConnectionCheckCache(`uber:${scopeKey}`);
    return result;
  },

  async connect(payload: UberConnectPayload) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/uber-direct/connect`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    const result = await handle<{
      connected: boolean;
      environment?: UberDirectEnvironment;
    }>(res, 'Failed to connect Uber Direct');
    clearConnectionCheckCache('uber:');
    return result;
  },

  async disconnect(businessAccountId?: string | null, workspaceId?: string | null) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/uber-direct/disconnect`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(scopeBody(businessAccountId, workspaceId)),
    });
    const result = await handle(res, 'Failed to disconnect Uber Direct');
    clearConnectionCheckCache('uber:');
    return result;
  },

  async createQuote(payload: UberQuotePayload) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/uber-direct/quote`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handle<{
      quote_id: string;
      fee: number; // integer, smallest currency unit
      currency: string;
      dropoff_eta?: string;
      expires?: string;
      duration?: number;
      pickup_duration?: number;
    }>(res, 'Failed to create quote');
  },

  async createDelivery(payload: UberCreateDeliveryPayload) {
    const res = await fetch(`${API_CONFIG.BASE_URL}/uber-direct/deliveries`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });
    return handle<{
      id?: string;
      delivery_id?: string;
      quote_id?: string;
      status?: string;
      complete?: boolean;
      fee?: number;
      currency?: string;
      tracking_url?: string;
      pickup_eta?: string;
      dropoff_eta?: string;
    }>(res, 'Failed to create delivery');
  },

  async listDeliveries(businessAccountId?: string | null, params?: UberListParams, workspaceId?: string | null) {
    const query = appendScope(new URLSearchParams(), businessAccountId, workspaceId);
    if (params?.limit) query.append('limit', params.limit.toString());
    if (params?.offset) query.append('offset', params.offset.toString());
    if (params?.status) query.append('status', params.status);
    const url = `${API_CONFIG.BASE_URL}/uber-direct/deliveries?${query.toString()}`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    return handle<{
      items?: unknown[];
      data?: unknown[];
      count?: number;
      total?: number;
      statusCounts?: Record<string, number>;
    }>(
      res,
      'Failed to list deliveries'
    );
  },

  async getDelivery(businessAccountId: string | null | undefined, deliveryId: string, workspaceId?: string | null) {
    const q = appendScope(new URLSearchParams(), businessAccountId, workspaceId);
    const url = `${API_CONFIG.BASE_URL}/uber-direct/deliveries/${encodeURIComponent(deliveryId)}?${q.toString()}`;
    const res = await fetch(url, { headers: getAuthHeaders() });
    const result = await handle<{ delivery?: UberDeliveryDetail }>(
      res,
      'Failed to refresh delivery'
    );
    if (result.ok && result.delivery && typeof result.delivery === 'object') {
      return { ok: true, ...(result.delivery as object) } as UberDirectResult<UberDeliveryDetail>;
    }
    return result as unknown as UberDirectResult<UberDeliveryDetail>;
  },

  async cancelDelivery(businessAccountId: string | null | undefined, deliveryId: string, workspaceId?: string | null) {
    const res = await fetch(
      `${API_CONFIG.BASE_URL}/uber-direct/deliveries/${encodeURIComponent(deliveryId)}/cancel`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(scopeBody(businessAccountId, workspaceId)),
      }
    );
    return handle<{ status?: string }>(res, 'Failed to cancel delivery');
  },

  async getProofOfDelivery(
    businessAccountId: string | null | undefined,
    deliveryId: string,
    opts?: { waypoint?: UberProofWaypoint; type?: UberProofType },
    workspaceId?: string | null
  ) {
    const res = await fetch(
      `${API_CONFIG.BASE_URL}/uber-direct/deliveries/${encodeURIComponent(deliveryId)}/proof-of-delivery`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          ...scopeBody(businessAccountId, workspaceId),
          waypoint: opts?.waypoint || 'dropoff',
          type: opts?.type || 'picture',
        }),
      }
    );
    return handle<UberProofOfDelivery>(res, 'Failed to fetch proof of delivery');
  },
};
